import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  Link,
  useTheme,
} from '@mui/material';
import {
  School,
  Gavel,
  Payment,
  Security,
  ContactMail,
} from '@mui/icons-material';

export default function TermsOfUse() {
  const theme = useTheme();

  const sections = [
    {
      id: '1',
      title: 'Purpose of ELMS',
      icon: <School color="primary" />,
      content: (
        <Typography variant="body1" paragraph>
          ELMS is an educational platform built to simulate the core functions of a physical classroom. 
          It provides tools for live lectures, digital resources, assessments, and student engagement. 
          ELMS exists to support learning and teaching, not as a money-making or investment platform.
        </Typography>
      )
    },
    {
      id: '2',
      title: 'Instructor Hubs and Classrooms',
      icon: <School color="primary" />,
      content: (
        <>
          <Typography variant="body1" paragraph>
            Upon registration, each instructor creates a Hub, representing their virtual school. 
            Within this Hub, instructors can set up multiple Classrooms to organize lectures, 
            assignments, assessments, and student interaction.
          </Typography>
          <Typography variant="body1" paragraph>
            Instructors are responsible for the content and structure of their classrooms.
          </Typography>
        </>
      )
    },
    {
      id: '3',
      title: 'Enrollment Fees and Pricing',
      icon: <Payment color="primary" />,
      content: (
        <>
          <Typography variant="body1" paragraph>
            Instructors may choose to set enrollment fees for their classrooms. ELMS does not place 
            restrictions on pricing.
          </Typography>
          <Typography variant="body1" paragraph>
            However, instructors in tertiary institutions should charge a fair fee that works for 
            both you and your students because you should consider the student might as well be 
            enrolled into other classrooms.
          </Typography>
          <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            Platform Fees:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="ELMS charges a 15% platform fee on paid enrollments." />
            </ListItem>
            <ListItem>
              <ListItemText primary="For classrooms with free enrollment, students may be charged a small fee (₦100) to reduce abuse/spam." />
            </ListItem>
          </List>
        </>
      )
    },
    {
      id: '4',
      title: 'Student Conduct and Responsibilities',
      icon: <Gavel color="primary" />,
      content: (
        <List dense>
          <ListItem>
            <ListItemText primary="Students must use the platform respectfully and only for educational purposes." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Sharing login details or attempting to bypass payment is strictly prohibited." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Students are responsible for maintaining their own internet connectivity and device functionality." />
          </ListItem>
        </List>
      )
    },
    {
      id: '5',
      title: 'Content Ownership',
      icon: <Security color="primary" />,
      content: (
        <List dense>
          <ListItem>
            <ListItemText primary="Instructors retain ownership of the original content they upload." />
          </ListItem>
          <ListItem>
            <ListItemText primary="By uploading content, instructors grant ELMS a license to display, store, and distribute the content within the platform for educational use." />
          </ListItem>
        </List>
      )
    },
    {
      id: '6',
      title: 'Payments and Refunds',
      icon: <Payment color="primary" />,
      content: (
        <List dense>
          <ListItem>
            <ListItemText primary="All payments are processed securely through integrated gateways." />
          </ListItem>
          <ListItem>
            <ListItemText primary="ELMS does not guarantee refunds. Refund policies, if applicable, are set by individual instructors." />
          </ListItem>
          <ListItem>
            <ListItemText primary="ELMS reserves the right to withhold payments if fraudulent or abusive activity is suspected." />
          </ListItem>
        </List>
      )
    },
    {
      id: '7',
      title: 'Account Suspension and Termination',
      icon: <Security color="primary" />,
      content: (
        <>
          <Typography variant="body1" paragraph>
            ELMS may suspend or terminate user accounts that:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="Violate these Terms" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Misuse the platform (e.g., for scams, harassment, or content theft)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Attempt to interfere with platform functionality" />
            </ListItem>
          </List>
          <Typography variant="body1" paragraph sx={{ mt: 2 }}>
            Instructors or students may also delete their account at any time.
          </Typography>
        </>
      )
    },
    {
      id: '8',
      title: 'Platform Availability',
      icon: <Security color="primary" />,
      content: (
        <Typography variant="body1" paragraph>
          While ELMS aims to provide uninterrupted access, we do not guarantee 100% uptime. 
          Occasional maintenance or server issues may occur. We appreciate your understanding and patience.
        </Typography>
      )
    },
    {
      id: '9',
      title: 'Changes to Terms',
      icon: <Gavel color="primary" />,
      content: (
        <Typography variant="body1" paragraph>
          We may update these Terms of Use from time to time. When we do, we will notify users 
          via email or platform notification. Continued use of the platform after changes 
          constitutes acceptance.
        </Typography>
      )
    },
    {
      id: '10',
      title: 'Contact Us',
      icon: <ContactMail color="primary" />,
      content: (
        <>
          <Typography variant="body1" paragraph>
            If you have questions about these Terms, please contact us at:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">📧</Typography>
              <Typography variant="body2">+2347059148543</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">🌐</Typography>
              <Link href="https://elms.ng" target="_blank" rel="noopener noreferrer">
                https://elms.ng
              </Link>
            </Box>
          </Box>
        </>
      )
    }
  ];

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            ELMS Terms of Use
          </Typography>
          <Chip 
            label="Effective Date: 24th April 2025" 
            color="primary" 
            variant="outlined"
            sx={{ fontSize: '0.9rem', px: 2 }}
          />
        </Box>

        {/* Introduction */}
        <Box sx={{ mb: 4, p: 3, backgroundColor: theme.palette.grey[50], borderRadius: 2 }}>
          <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7, textAlign: 'center' }}>
            Welcome to ELMS (E-Learning Management System). By accessing or using our platform, 
            you agree to be bound by these Terms of Use. Please read them carefully.
          </Typography>
        </Box>

        {/* Terms Sections */}
        {sections.map((section, index) => (
          <Box key={section.id} sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              {section.icon}
              <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                {section.id}. {section.title}
              </Typography>
            </Box>
            
            <Box sx={{ ml: 5 }}>
              {section.content}
            </Box>
            
            {index < sections.length - 1 && (
              <Divider sx={{ mt: 3, opacity: 0.3 }} />
            )}
          </Box>
        ))}

        {/* Footer */}
        <Box sx={{ mt: 6, pt: 3, borderTop: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            By using ELMS, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
}